import { useDroppable } from "@dnd-kit/core";
import { useSortable, SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Trash2, Copy, ArrowUp, ArrowDown, Sparkles, Bold, Italic, Underline, Link2, Save } from "lucide-react";
import { useCallback, useRef, useState } from "react";
import { SectionView } from "@/components/sections/SectionView";
import { sanitizeRichHtml } from "@/lib/richHtml";

// Widths that mimic real device viewports. Kept in JS (not Tailwind) so we can
// smoothly animate the canvas width when the user switches modes.
export const DEVICE_WIDTHS = { desktop: "100%", tablet: 768, mobile: 375 };

// A thin invisible drop zone we render between sections. Lets users drop a
// new block (dragged from the palette) at a specific index. Highlights when
// something is hovering over it.
function DropZone({ index, activePaletteKind }) {
  const { setNodeRef, isOver } = useDroppable({ id: `dropzone:${index}` });
  const active = !!activePaletteKind && isOver;
  return (
    <div
      ref={setNodeRef}
      className="relative"
      style={{ height: active ? 24 : 8, transition: "height 120ms ease" }}
      data-testid={`dropzone-${index}`}
    >
      {active && (
        <div className="absolute inset-x-6 top-1/2 -translate-y-1/2 h-1 rounded-full bg-[#0B5CFF]" />
      )}
    </div>
  );
}

function SectionFrame({ section, index, total, isSelected, onSelect, onDelete, onDuplicate, onMove, onAiRewrite, onSaveAsTemplate, onInlineEdit, accent }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: section.id,
    data: { source: "canvas" },
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  // ---- Rich inline editing -----------------------------------------------
  // Double-clicking a `data-inline-field` element inside SectionView converts
  // it to a full contentEditable (rich text — bold/italic/underline/link).
  // A tiny floating toolbar follows the selection while the field is active.
  // Commits go through the allow-list sanitiser before persisting.
  const frameRef = useRef(null);
  const originalHtmlRef = useRef("");
  const [toolbar, setToolbar] = useState(null); // { x, y, field, target } | null

  const positionToolbar = (target) => {
    const rect = target.getBoundingClientRect();
    // Toolbar sits just above the element, 6px gap. clamp within viewport.
    const x = Math.min(Math.max(rect.left + rect.width / 2, 130), window.innerWidth - 130);
    const y = Math.max(rect.top - 44, 8);
    setToolbar((prev) => ({ ...(prev || {}), x, y, target }));
  };

  const closeToolbar = () => setToolbar(null);

  const handleDblClick = useCallback((e) => {
    const target = e.target.closest("[data-inline-field]");
    if (!target || !frameRef.current?.contains(target)) return;
    const field = target.getAttribute("data-inline-field");
    if (!field) return;
    e.stopPropagation();
    originalHtmlRef.current = target.innerHTML;
    // Prefer semantic <b>/<i>/<u> over styled spans — much cleaner sanitised
    // output and browser-portable. This has to be set BEFORE the first
    // execCommand runs, which happens after focus.
    try { document.execCommand("styleWithCSS", false, false); } catch (_) { /* older browsers */ }
    target.setAttribute("contenteditable", "true");
    target.classList.add("vp-inline-editing");
    target.focus();
    const range = document.createRange();
    range.selectNodeContents(target);
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
    positionToolbar(target);
    setToolbar({ x: 0, y: 0, field, target });
    positionToolbar(target);

    let done = false;  // guard so a stray blur after Escape can't overwrite
    const cleanup = (commit) => {
      if (done) return;
      done = true;
      target.removeAttribute("contenteditable");
      target.classList.remove("vp-inline-editing");
      target.removeEventListener("blur", onBlurHandler);
      target.removeEventListener("keydown", onKey);
      closeToolbar();
      if (commit) {
        const clean = sanitizeRichHtml(target.innerHTML);
        if (clean !== originalHtmlRef.current) onInlineEdit(field, clean);
      } else {
        target.innerHTML = originalHtmlRef.current;
      }
    };
    const onBlurHandler = (ev) => {
      const to = ev.relatedTarget;
      if (to && to.closest && to.closest("[data-inline-toolbar]")) {
        setTimeout(() => target.focus(), 0);
        return;
      }
      cleanup(true);
    };
    const onKey = (ev) => {
      const mod = ev.metaKey || ev.ctrlKey;
      if (mod && ev.key.toLowerCase() === "b") { ev.preventDefault(); document.execCommand("bold"); }
      else if (mod && ev.key.toLowerCase() === "i") { ev.preventDefault(); document.execCommand("italic"); }
      else if (mod && ev.key.toLowerCase() === "u") { ev.preventDefault(); document.execCommand("underline"); }
      else if (mod && ev.key.toLowerCase() === "k") { ev.preventDefault(); promptLink(); }
      else if (ev.key === "Enter" && !ev.shiftKey) { ev.preventDefault(); target.blur(); }
      else if (ev.key === "Escape") { ev.preventDefault(); cleanup(false); }
    };
    // Escape is captured at document-level too so it wins over the blur race.
    const onEscapeCapture = (ev) => {
      if (ev.key === "Escape" && !done) { ev.preventDefault(); cleanup(false); document.removeEventListener("keydown", onEscapeCapture, true); }
    };
    document.addEventListener("keydown", onEscapeCapture, true);
    target.addEventListener("blur", onBlurHandler);
    target.addEventListener("keydown", onKey);
  }, [onInlineEdit]);

  const promptLink = () => {
    const url = window.prompt("Link URL (leave empty to remove link):", "");
    if (url === null) return; // user cancelled
    if (url.trim() === "") document.execCommand("unlink");
    else document.execCommand("createLink", false, url.trim());
  };

  const runCmd = (cmd) => {
    // The button click focuses the toolbar; restore selection on the
    // editable target before running the command.
    if (toolbar?.target) toolbar.target.focus();
    if (cmd === "link") { promptLink(); return; }
    document.execCommand(cmd);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={(e) => { e.stopPropagation(); onSelect(); }}
      onDoubleClick={handleDblClick}
      className={"relative group cursor-pointer transition-shadow " +
        (isSelected ? "ring-2 ring-[#0B5CFF] ring-offset-2 ring-offset-white" : "hover:ring-1 hover:ring-[#B7CBF5] hover:ring-offset-2 hover:ring-offset-white")}
      data-testid={`canvas-section-${section.id}`}
      data-selected={isSelected ? "true" : "false"}
    >
      {/* Hover / selected controls */}
      <div className={"absolute -top-3 left-3 z-20 flex items-center gap-1 " +
        (isSelected ? "opacity-100" : "opacity-0 group-hover:opacity-100 transition-opacity")}>
        <button
          {...attributes} {...listeners}
          type="button"
          onClick={(e) => e.stopPropagation()}
          className="h-6 w-6 rounded-md bg-[#0B0D17] text-white flex items-center justify-center cursor-grab active:cursor-grabbing shadow-lg"
          title="Drag to reorder"
          data-testid={`section-drag-${section.id}`}
        >
          <GripVertical className="w-3 h-3" strokeWidth={2} />
        </button>
        <span className="h-6 px-2 rounded-md bg-[#0B0D17] text-white text-[10px] font-bold uppercase tracking-wider flex items-center shadow-lg">
          {section.kind}
        </span>
      </div>
      <div className={"absolute -top-3 right-3 z-20 flex items-center gap-1 " +
        (isSelected ? "opacity-100" : "opacity-0 group-hover:opacity-100 transition-opacity")}>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onMove(index - 1); }}
          disabled={index === 0}
          className="h-6 w-6 rounded-md bg-white border border-[#E4E5E9] text-[#0B0D17] hover:bg-[#F1F3F7] flex items-center justify-center disabled:opacity-40 shadow-sm"
          title="Move up"
          data-testid={`section-up-${section.id}`}
        >
          <ArrowUp className="w-3 h-3" strokeWidth={2} />
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onMove(index + 1); }}
          disabled={index === total - 1}
          className="h-6 w-6 rounded-md bg-white border border-[#E4E5E9] text-[#0B0D17] hover:bg-[#F1F3F7] flex items-center justify-center disabled:opacity-40 shadow-sm"
          title="Move down"
          data-testid={`section-down-${section.id}`}
        >
          <ArrowDown className="w-3 h-3" strokeWidth={2} />
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onAiRewrite(); }}
          className="h-6 px-2 rounded-md bg-gradient-to-r from-[#0B5CFF] to-[#6D28D9] text-white hover:opacity-90 flex items-center gap-1 shadow-sm"
          title="AI rewrite (1 credit)"
          data-testid={`section-ai-${section.id}`}
        >
          <Sparkles className="w-3 h-3" strokeWidth={2} /> <span className="text-[10px] font-semibold">AI</span>
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onSaveAsTemplate(); }}
          className="h-6 w-6 rounded-md bg-white border border-[#E4E5E9] text-[#0B0D17] hover:bg-[#F1F3F7] flex items-center justify-center shadow-sm"
          title="Save as template"
          data-testid={`section-save-template-${section.id}`}
        >
          <Save className="w-3 h-3" strokeWidth={2} />
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDuplicate(); }}
          className="h-6 w-6 rounded-md bg-white border border-[#E4E5E9] text-[#0B0D17] hover:bg-[#F1F3F7] flex items-center justify-center shadow-sm"
          title="Duplicate"
          data-testid={`section-duplicate-${section.id}`}
        >
          <Copy className="w-3 h-3" strokeWidth={2} />
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="h-6 w-6 rounded-md bg-white border border-[#E4E5E9] text-[#B00020] hover:bg-[#FDE8EA] flex items-center justify-center shadow-sm"
          title="Delete"
          data-testid={`section-delete-${section.id}`}
        >
          <Trash2 className="w-3 h-3" strokeWidth={2} />
        </button>
      </div>

      {/* The actual rendered section (same component as the public page — DRY) */}
      <div ref={frameRef} className="pointer-events-auto">
        <SectionView section={section} accent={accent} />
      </div>

      {/* Floating rich-text toolbar (bold / italic / underline / link) */}
      {toolbar && (
        <div
          data-inline-toolbar="true"
          className="fixed z-[80] -translate-x-1/2 bg-[#0B0D17] text-white rounded-lg shadow-2xl flex items-center gap-0.5 p-1"
          style={{ left: toolbar.x, top: toolbar.y }}
          onMouseDown={(e) => e.preventDefault()}  // don't steal focus from the editable
        >
          <ToolbarBtn Icon={Bold} title="Bold (Ctrl+B)" onClick={() => runCmd("bold")} tid="rt-bold" />
          <ToolbarBtn Icon={Italic} title="Italic (Ctrl+I)" onClick={() => runCmd("italic")} tid="rt-italic" />
          <ToolbarBtn Icon={Underline} title="Underline (Ctrl+U)" onClick={() => runCmd("underline")} tid="rt-underline" />
          <div className="w-px h-4 bg-white/20 mx-0.5" />
          <ToolbarBtn Icon={Link2} title="Link (Ctrl+K)" onClick={() => runCmd("link")} tid="rt-link" />
        </div>
      )}
    </div>
  );
}

// Small helper for the floating toolbar buttons — keeps the JSX above readable.
function ToolbarBtn({ Icon, title, onClick, tid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      data-testid={tid}
      className="h-7 w-7 rounded-md hover:bg-white/10 inline-flex items-center justify-center"
    >
      <Icon className="w-3.5 h-3.5" strokeWidth={2} />
    </button>
  );
}

export function Canvas({ sections, selectedId, onSelect, onDelete, onDuplicate, onMove, onAiRewrite, onSaveAsTemplate, onInlineEdit, device, accent, activePaletteKind, onDeselect }) {
  const width = DEVICE_WIDTHS[device] || "100%";
  return (
    <div className="flex-1 overflow-auto bg-[#F1F3F7] p-6" onClick={onDeselect} data-testid="canvas-scroll">
      <div
        className="mx-auto bg-white rounded-2xl shadow-xl overflow-hidden transition-all duration-300"
        style={{ maxWidth: width, width: "100%" }}
        data-testid={`canvas-frame-${device}`}
      >
        <SortableContext items={sections.map((s) => s.id)} strategy={verticalListSortingStrategy}>
          <DropZone index={0} activePaletteKind={activePaletteKind} />
          {sections.map((sec, i) => (
            <div key={sec.id}>
              <SectionFrame
                section={sec}
                index={i}
                total={sections.length}
                isSelected={selectedId === sec.id}
                onSelect={() => onSelect(sec.id)}
                onDelete={() => onDelete(sec.id)}
                onDuplicate={() => onDuplicate(sec.id)}
                onMove={(newIdx) => onMove(sec.id, newIdx)}
                onAiRewrite={() => onAiRewrite(sec.id)}
                onSaveAsTemplate={() => onSaveAsTemplate(sec.id)}
                onInlineEdit={(field, value) => onInlineEdit(sec.id, field, value)}
                accent={accent}
              />
              <DropZone index={i + 1} activePaletteKind={activePaletteKind} />
            </div>
          ))}
          {sections.length === 0 && (
            <div className="p-16 text-center">
              <div className="text-sm text-[#747680]">Drag a block from the left or click the <b>+</b> to start building.</div>
            </div>
          )}
        </SortableContext>
      </div>
    </div>
  );
}
